import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import ComplexTranslate from 'components/ComplexTranslate';
import { CountDown } from 'components/common/CountDown/CountDown';
import {
  CONFIRMATION_HANDLE_TYPES,
  RESEND_CODE_ERROR_CODE_DISABLE,
  TWO_FACTOR_AUTH_TYPES
} from 'components/common/constants';
import { getTimerDurationSecondsLeft } from 'components/utils';
import { getConfirmationActionType, getTimerDurationExpiresAt } from 'services/authUtils';
import Alert from 'uikit/Alert/Alert';
import { SecurityInput } from 'uikit/SecurityInput/SecurityInput';
import './ConfirmCodeWrapper.scss';

const RESEND_TIMEOUT = 60;

export const ConfirmCodeWrapper = ({
  type = getConfirmationActionType(),
  PhoneComponent,
  isDisabled,
  error,
  onComplete,
  showCountDown,
  handleResendCode,
  onTimerEnd,
  alertState,
  isSwitchToAnotherConfirmationMethod = false,
  isMessenger = false,
  isCardCreationForm = false,
  isGoogle2FASignUpPopUp = false,
  handleSwitchToAnotherMethod,
  confirmationHandleType = CONFIRMATION_HANDLE_TYPES.ON_COMPLETE
}) => {
  const i18n = useContext(i18nContext);
  const timerDurationExpiresAt = getTimerDurationExpiresAt();
  const [timerDurationSecondsLeft, setTimerDurationSecondsLeft] = useState(
    timerDurationExpiresAt && timerDurationExpiresAt !== 'undefined'
      ? getTimerDurationSecondsLeft(timerDurationExpiresAt)
      : RESEND_TIMEOUT
  );
  const [isDisabledResendCode, setIsDisabledResendCode] = useState(
    RESEND_CODE_ERROR_CODE_DISABLE?.includes(error?.code)
  );

  useEffect(() => {
    if (timerDurationExpiresAt && timerDurationExpiresAt !== 'undefined') {
      setTimerDurationSecondsLeft(getTimerDurationSecondsLeft(timerDurationExpiresAt));
    } else {
      setTimerDurationSecondsLeft(RESEND_TIMEOUT);
    }
  }, [timerDurationExpiresAt]);

  useEffect(() => {
    if (error?.code && RESEND_CODE_ERROR_CODE_DISABLE?.includes(error?.code)) {
      setIsDisabledResendCode(true);
    } else {
      setIsDisabledResendCode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error?.code]);

  return (
    <div
      className={
        isMessenger
          ? 'sms-code-wrapper-messenger'
          : isCardCreationForm
            ? 'sms-code-wrapper-card-creation-form'
            : 'sms-code-wrapper'
      }
    >
      <Alert type={alertState.type} message={i18n.getMessage(alertState.message)} />
      <div className={isMessenger ? 'sms-description-messenger' : 'sms-description'}>
        <ComplexTranslate
          text={
            type === TWO_FACTOR_AUTH_TYPES.SMS
              ? 'sms.description.sms'
              : type === TWO_FACTOR_AUTH_TYPES.WHATSAPP
                ? 'sms.description.whatsapp'
                : type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION
                  ? 'sms.description.pushNotification'
                  : isGoogle2FASignUpPopUp
                    ? '2fa.signup.description'
                    : '2fa.auth.description'
          }
          interpolation={{
            phone: PhoneComponent
          }}
        />
      </div>
      {type !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && (
        <SecurityInput
          disabled={isDisabled}
          type={'sms'}
          error={alertState.type}
          onComplete={onComplete}
          confirmationHandleType={confirmationHandleType}
        />
      )}
      {(isSwitchToAnotherConfirmationMethod ||
        (!isSwitchToAnotherConfirmationMethod &&
          (type === TWO_FACTOR_AUTH_TYPES.SMS || type === TWO_FACTOR_AUTH_TYPES.WHATSAPP))) && (
        <div className={'resend-wrapper'}>
          {!showCountDown ? (
            <ComplexTranslate
              text={'sms.description.resend'}
              interpolation={{
                resendCode:
                  type === TWO_FACTOR_AUTH_TYPES.SMS || type === TWO_FACTOR_AUTH_TYPES.WHATSAPP ? (
                    <>
                      <button
                        className={'unset link-normal'}
                        disabled={isDisabledResendCode}
                        onClick={(e) => handleResendCode(e)}
                      >
                        {i18n.getMessage('sms.resendCode')}
                      </button>
                      {isSwitchToAnotherConfirmationMethod && (
                        <>
                          <br />
                          <button
                            className={'unset link-normal without-margin'}
                            onClick={handleSwitchToAnotherMethod}
                            disabled={isDisabledResendCode}
                          >
                            {i18n.getMessage('sms.switchToAnotherMethod')}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {type !== TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH && (
                        <span> {i18n.getMessage('sms.otherMethods')}</span>
                      )}
                      {isSwitchToAnotherConfirmationMethod && (
                        <>
                          <br />
                          <button
                            className={'unset link-normal without-margin'}
                            onClick={handleSwitchToAnotherMethod}
                            disabled={isDisabledResendCode}
                          >
                            {i18n.getMessage('sms.switchToAnotherMethod')}
                          </button>
                        </>
                      )}
                    </>
                  )
              }}
            />
          ) : (
            <>
              {type !== TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH && (
                <p className={'resend-info'}>
                  {i18n.getMessage('sms.resentMessage') + ' '}
                  <CountDown onEnd={onTimerEnd} duration={timerDurationSecondsLeft} isDisabled={isDisabled} />
                </p>
              )}

              {isSwitchToAnotherConfirmationMethod && (
                <>
                  <button
                    className={'unset link-normal without-margin'}
                    onClick={handleSwitchToAnotherMethod}
                    disabled={isDisabledResendCode}
                  >
                    {i18n.getMessage('sms.switchToAnotherMethod')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

ConfirmCodeWrapper.propTypes = {
  type: PropTypes.string,
  PhoneComponent: PropTypes.object,
  isDisabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onComplete: PropTypes.func,
  showCountDown: PropTypes.bool,
  handleResendCode: PropTypes.func,
  onTimerEnd: PropTypes.func,
  isSwitchToAnotherConfirmationMethod: PropTypes.bool,
  isMessenger: PropTypes.bool,
  isCardCreationForm: PropTypes.bool,
  isGoogle2FASignUpPopUp: PropTypes.bool,
  handleSwitchToAnotherMethod: PropTypes.func,
  confirmationHandleType: PropTypes.string
};
